import React from 'react';
import AgreementModal from './AgreementModal';
import PrivateSaleConfirmationModal from './PrivateSaleConfirmationModal';
import { PrivateSaleModalName } from './config';
import OrderDetailModal from './OrderDetailModal';
import RefundedDetailModal from './RefundedDetailModal';
import { navigateToConnectWallet, navigateToPrivateSaleCheckoutPage, navigateToProjectDetail } from 'src/utils/navigate';
import { useTypedSelector } from 'src/redux/utils';
import ConnectWalletModal from './ConnectWalletModal';
import { RouteName } from 'src/routes/config';
import WalletConnectConfirmProvider from 'src/Components/WalletConnectConfirmProvider';

const noop = () => {};

const PrivateSaleContext = /*#__PURE__*/React.createContext({
  openModal: noop,
  showOrderDetail: noop,
  showRefundedDetail: noop,
  confirmOrder: noop,
  closeModal: noop,
  showAgreement: noop
});

const PrivateSaleProvider = ({
  children
}) => {
  const [modal, setModal] = React.useState(null);
  const [privateSaleInfo, setPrivateSaleInfo] = React.useState();
  const [projectId, setProjectId] = React.useState();
  const [symbol, setSymbol] = React.useState();
  const openModal = React.useCallback(modal => {
    setModal(modal);
  }, []);
  const closeModal = React.useCallback(() => {
    setModal(null);
    setProjectId(undefined);
    setPrivateSaleInfo(undefined);
    setSymbol(undefined);
  }, []);
  const showAgreement = React.useCallback(id => {
    openModal(PrivateSaleModalName.CONNECT_WALLET);
    setProjectId(id);
  }, []);
  const confirmOrder = React.useCallback(order => {
    openModal(PrivateSaleModalName.CONFIRM_ORDER);
    setPrivateSaleInfo(order);
  }, []);
  const showOrderDetail = React.useCallback((id, symbol) => {
    setProjectId(id);
    setSymbol(symbol);
    openModal(PrivateSaleModalName.ORDER_DETAIL);
  }, []);
  const showRefundedDetail = React.useCallback((id, symbol) => {
    setProjectId(id);
    setSymbol(symbol);
    openModal(PrivateSaleModalName.REFUNDED_DETAIL);
  }, []);
  const signedIn = useTypedSelector(state => !!state.userSignIn.accessToken);
  React.useEffect(() => {
    if (signedIn && modal === PrivateSaleModalName.CONNECT_WALLET) {
      openModal(PrivateSaleModalName.AGREEMENT);
    }
  }, [signedIn, modal]);
  const context = React.useMemo(() => {
    return {
      modal: modal || undefined,
      openModal,
      closeModal,
      showAgreement,
      confirmOrder,
      showOrderDetail,
      showRefundedDetail
    };
  }, [modal]); // Render the current modal

  const renderModal = () => {
    switch (modal) {
      case PrivateSaleModalName.CONNECT_WALLET:
        return /*#__PURE__*/React.createElement(ConnectWalletModal, {
          onClose: () => {
            closeModal();
          },
          onConnectButtonPressed: () => {
            navigateToConnectWallet({
              redirectTo: `${RouteName.ProjectDetail}/${projectId}`
            });
          }
        });

      case PrivateSaleModalName.AGREEMENT:
        return /*#__PURE__*/React.createElement(AgreementModal, {
          onSubmit: () => {
            closeModal();
            projectId && navigateToPrivateSaleCheckoutPage(projectId, true);
          },
          onClose: () => {
            closeModal();
          }
        });

      case PrivateSaleModalName.CONFIRM_ORDER:
        return privateSaleInfo && /*#__PURE__*/React.createElement(PrivateSaleConfirmationModal, {
          data: privateSaleInfo,
          onClose: () => {
            closeModal();
          },
          onSubmit: (id, symbol) => {
            showOrderDetail(id, symbol);
          }
        });

      case PrivateSaleModalName.ORDER_DETAIL:
        return projectId && symbol && /*#__PURE__*/React.createElement(OrderDetailModal, {
          projectId: projectId,
          symbol: symbol,
          onClose: () => {
            navigateToProjectDetail(projectId);
            closeModal();
          }
        });

      case PrivateSaleModalName.REFUNDED_DETAIL:
        return projectId && symbol && /*#__PURE__*/React.createElement(RefundedDetailModal, {
          projectId: projectId,
          symbol: symbol,
          onClose: () => {
            closeModal();
          }
        });

      default:
        return null;
    }
  };

  return /*#__PURE__*/React.createElement(WalletConnectConfirmProvider, null, /*#__PURE__*/React.createElement(PrivateSaleContext.Provider, {
    value: context
  }, children, renderModal()));
};

export const usePrivateSale = () => {
  const {
    modal,
    showAgreement,
    confirmOrder,
    showOrderDetail,
    showRefundedDetail
  } = React.useContext(PrivateSaleContext);
  return {
    modal,
    showAgreement,
    confirmOrder,
    showOrderDetail,
    showRefundedDetail
  };
};
export default PrivateSaleProvider;